/**
 * Flexible-dates math and labels — SBP parity slice 6 (#870, Eli decision 3).
 *
 * Transcribed from the toolkit's inline flex bridge inside Searchbar Pro's own
 * date picker (`assets/js/searchbar-pro.js`, `initFlexMode`, js:1021-1369) —
 * NOT from the sitewide `FlexibleSearch` PHP module, which patches a different
 * surface (the core Explorer date modal) and whose `UrlMapper` disagrees with
 * the bridge about one-month checkouts. See `representativeRange` below.
 *
 * Everything here is pure and works in `YYYY-MM-DD` strings, exactly like the
 * reference, so the whole surface is testable without a DOM. Callers convert at
 * the boundary with `new Date(iso + 'T00:00:00')` — the widget's existing local-
 * midnight idiom (providers.tsx, DateFilter.tsx).
 */
import { format } from 'date-fns'

/** The four stay lengths the reference offers (`Settings::VALID_STAY_LENGTHS`,
 *  includes/FlexibleSearch/Settings.php:27). Order is the reference's. */
export const FLEX_STAY_LENGTHS = ['weekend', '1w', '2w', '1m'] as const
export type FlexStayLength = (typeof FLEX_STAY_LENGTHS)[number]

/** The "no particular month" month-strip option. Reference: `ANYTIME`
 *  (searchbar-pro.js:1035) — a sentinel in the same slot as a `YYYY-MM`. */
export const FLEX_ANYTIME = 'anytime'

/** Stay-length chip labels, from the toolkit's own map
 *  (SearchbarPro/Shortcode.php:618-623). English literals: the widget has no
 *  translation layer, and the surrounding filter components hardcode their
 *  strings the same way. */
export const FLEX_STAY_LABELS: Record<FlexStayLength, string> = {
  weekend: 'Weekend stay',
  '1w': '1 week',
  '2w': '2 weeks',
  '1m': '1 month',
}

/** Suggested-dates headings, per stay length (Shortcode.php:762-765). */
export const FLEX_ALT_HEADINGS: Record<FlexStayLength, string> = {
  weekend: 'Suggested weekends:',
  '1w': 'Suggested weeks:',
  '2w': 'Suggested 2-week stays:',
  '1m': 'Suggested months:',
}

export function isFlexStayLength(v: unknown): v is FlexStayLength {
  return typeof v === 'string' && (FLEX_STAY_LENGTHS as readonly string[]).includes(v)
}

/**
 * `anytime`, or a well-formed `YYYY-MM`. Reference guard: searchbar-pro.js:1353.
 *
 * The month number is range-checked, not merely shape-checked. `parseFlexFromUrl`
 * (providers.tsx) is the only path by which an untrusted month reaches state, and
 * a regex-valid but nonexistent one — `?flex_month=2026-13` — used to survive it:
 * `monthLabel` then hands `new Date('2026-13-01T00:00:00')` (Invalid Date) to
 * date-fns `format`, which throws RangeError and takes the whole widget down.
 * A rejected month degrades to Anytime, the reference's own fallback.
 *
 * The year is bounded ABOVE at today's year + 2, and the bound is load-bearing,
 * not cosmetic: `?flex_month=9999-12` used to hang `enumerateAlts` on page load.
 * Stepping past 9999-12-31 overflows into year 10000, where `toISOString()`
 * yields `'+010000-…'` — a string whose 10-char slice compares lexicographically
 * BELOW every `'9999-…'` date, so the weekly walk's `cur <= end` never goes
 * false (unbounded loop, tab OOM). +2 years mirrors the picker's own horizon:
 * the month strip never offers more than `MAX_FORWARD_MONTHS - 1` (22) months
 * out (DateFilter.tsx), so no strip-made URL can land later than that.
 *
 * Deliberately NOT bounding the year below, and NOT rejecting months outside
 * the configured strip: a past month is APPROVED input — `representativeRange`
 * clamps it to today on purpose (pinned by its own test), so rejecting it would
 * break shipped behaviour — and a past walk always terminates.
 */
export function isFlexMonth(v: unknown, today: string = todayIso()): v is string {
  if (typeof v !== 'string') return false
  if (v === FLEX_ANYTIME) return true
  const m = /^(\d{4})-(\d{2})$/.exec(v)
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return false
  return Number(m[1]) <= Number(today.slice(0, 4)) + 2
}

/** Local today as `YYYY-MM-DD`. Reference `todayIso` (searchbar-pro.js:252-257)
 *  reads the LOCAL calendar day, not UTC — the whole month strip is built in
 *  the visitor's own timezone. */
export function todayIso(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd')
}

// --- date arithmetic (reference: fAddDays / fNextDow / fMonthPlus) ----------
// Parsed as UTC midnight so day-of-month arithmetic can never be shifted by a
// DST transition, then formatted straight back to a date string. Identical to
// the reference, which does the same for the same reason.

export function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** The first `dow` on or AFTER `iso` (0=Sun … 5=Fri, 6=Sat). Returns `iso`
 *  itself when it already falls on that weekday — the `% 7` makes the step 0. */
export function nextDow(iso: string, dow: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + ((dow - d.getUTCDay() + 7) % 7))
  return d.toISOString().slice(0, 10)
}

/**
 * Shift by whole calendar months, keeping the day-of-month.
 *
 * Inherits `setUTCMonth`'s overflow: a day-of-month the target month does not
 * have rolls forward (2026-01-31 + 1 → 2026-03-03). The reference has exactly
 * this behaviour (`fMonthPlusOne`, searchbar-pro.js:1047-1051) and it is
 * reachable — the "1 month" anchor is today's date whenever the visitor picks
 * Anytime or the current month, so a search started on the 31st produces a
 * 31-night "1 month" stay. Transcribed rather than corrected: this is the
 * defaulted calendar-bound semantics, and clamping to the month end here would
 * put the port somewhere neither toolkit implementation lives.
 */
export function monthPlus(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCMonth(d.getUTCMonth() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * The concrete date range a flexible selection searches.
 *
 * Reference `representativeRange` (searchbar-pro.js:1057-1069). The server
 * pipeline never learns the search was flexible — it receives these two dates
 * like any exact search, and `flex`/`stay_length`/`flex_month` ride the URL
 * purely so the flexible UI can rehydrate.
 *
 * ONE-MONTH MATH: **calendar-bound** — checkout is the same day of the next
 * month (`monthPlus(ci, 1)`). The toolkit contains two divergent
 * implementations of this: the JS bridge ported here is calendar-bound, while
 * `FlexibleSearch\UrlMapper` defaults to a fixed 30 days. #870's inventory
 * flagged the conflict and no pick was made, so the bridge — the surface this
 * whole slice is a port OF — is the default. Flipping to fixed-30 is a
 * one-line change here (`addDays(ci, 30)`).
 */
export function representativeRange(
  stay: FlexStayLength,
  month: string,
  today: string,
): { checkin: string; checkout: string } {
  const requested = month === FLEX_ANYTIME ? today : `${month}-01`
  // A month already underway (or past) anchors on today, never on a date the
  // visitor could not book.
  const anchor = requested < today ? today : requested
  if (stay === 'weekend') {
    const ci = nextDow(anchor, 5)
    return { checkin: ci, checkout: addDays(ci, 2) }
  }
  if (stay === '1w') {
    const ci = nextDow(anchor, 6)
    return { checkin: ci, checkout: addDays(ci, 7) }
  }
  if (stay === '2w') {
    const ci = nextDow(anchor, 6)
    return { checkin: ci, checkout: addDays(ci, 14) }
  }
  return { checkin: anchor, checkout: monthPlus(anchor, 1) }
}

// --- labels ----------------------------------------------------------------
// date-fns rather than `Intl` (which DateFilter's `formatShortDate` prefers):
// these strings are compared against each other inside one chip track, so a
// deterministic abbreviation matters more than locale-awareness here. The
// reference is deterministic too — it slices its localized month names to three
// characters.

/** Month-strip chip label, e.g. "Aug 2026" (reference `monthLabel`, js:1073). */
export function monthLabel(ym: string): string {
  return format(new Date(ym + '-01T00:00:00'), 'MMM yyyy')
}

/**
 * Suggested-dates chip label, e.g. "Aug 14 – 16" or "Aug 30 – Sep 6".
 * Reference `altRangeLabel` (js:1163-1169): the range is printed to the LAST
 * NIGHT, not the checkout morning, and the second month is omitted when both
 * ends fall in the same one.
 */
export function altRangeLabel(checkin: string, checkout: string): string {
  const last = addDays(checkout, -1)
  const ci = new Date(checkin + 'T00:00:00')
  const lastDate = new Date(last + 'T00:00:00')
  const sameMonth = checkin.slice(0, 7) === last.slice(0, 7)
  const head = `${format(ci, 'MMM')} ${ci.getDate()}`
  const tail = sameMonth ? `${lastDate.getDate()}` : `${format(lastDate, 'MMM')} ${lastDate.getDate()}`
  return `${head} – ${tail}`
}

export type FlexAlt = { checkin: string; checkout: string; label: string }

/**
 * The suggested-dates chip set for a flexible selection. Reference
 * `enumerateAlts` (searchbar-pro.js:1170-1196).
 *
 * Weekend/1w/2w walk a weekday stride — from today when the month is Anytime,
 * otherwise across the chosen month. The month stay instead offers whole
 * months: the next six when Anytime, or the chosen month with its neighbours.
 * Anything already in the past is dropped.
 */
export function enumerateAlts(
  stay: FlexStayLength,
  month: string,
  today: string,
): FlexAlt[] {
  const weekly = (dow: number, nights: number, stride: number, count: number): FlexAlt[] => {
    const out: FlexAlt[] = []
    if (month === FLEX_ANYTIME) {
      let cur = nextDow(today, dow)
      for (let i = 0; i < count; i++) {
        out.push({ checkin: cur, checkout: addDays(cur, nights), label: '' })
        cur = addDays(cur, stride)
      }
      return out
    }
    const end = addDays(monthPlus(`${month}-01`, 1), -1)
    let cur = nextDow(`${month}-01`, dow)
    while (cur <= end) {
      out.push({ checkin: cur, checkout: addDays(cur, nights), label: '' })
      cur = addDays(cur, stride)
    }
    return out
  }

  let raw: FlexAlt[]
  if (stay === 'weekend') raw = weekly(5, 2, 7, 8)
  else if (stay === '1w') raw = weekly(6, 7, 7, 8)
  else if (stay === '2w') raw = weekly(6, 14, 14, 6)
  else {
    raw = []
    if (month === FLEX_ANYTIME) {
      for (let i = 0; i < 6; i++) {
        const s = monthPlus(`${today.slice(0, 7)}-01`, i)
        raw.push({ checkin: s, checkout: monthPlus(s, 1), label: monthLabel(s.slice(0, 7)) })
      }
    } else {
      for (let i = -1; i <= 1; i++) {
        const s = monthPlus(`${month}-01`, i)
        raw.push({ checkin: s, checkout: monthPlus(s, 1), label: monthLabel(s.slice(0, 7)) })
      }
    }
  }

  return raw
    .filter((c) => c.checkin >= today)
    .map((c) => ({ ...c, label: c.label || altRangeLabel(c.checkin, c.checkout) }))
}

/** The month-strip options: Anytime, then `monthsAhead` months from the
 *  current one (reference `buildMonths`, js:1078-1099). */
export function monthOptions(monthsAhead: number, today: string): { key: string; label: string }[] {
  const items = [{ key: FLEX_ANYTIME, label: 'Anytime' }]
  const year = parseInt(today.slice(0, 4), 10)
  const monthIdx = parseInt(today.slice(5, 7), 10) - 1
  for (let i = 0; i < monthsAhead; i++) {
    const d = new Date(Date.UTC(year, monthIdx + i, 1))
    const ym = d.toISOString().slice(0, 7)
    items.push({ key: ym, label: monthLabel(ym) })
  }
  return items
}

/**
 * The WHEN trigger label for an applied flexible search, e.g.
 * "1 week · Aug 15 – Aug 22". Reference `flexLabel` (js:1121-1135): it shows
 * the dates actually being searched rather than the abstract selection,
 * falling back to the month word before a range exists.
 */
export function flexTriggerLabel(
  stay: FlexStayLength,
  month: string,
  checkin: string | undefined,
  checkout: string | undefined,
  formatDay: (d: Date) => string,
): string {
  const stayText = FLEX_STAY_LABELS[stay]
  if (checkin && checkout) {
    const from = formatDay(new Date(checkin + 'T00:00:00'))
    const to = formatDay(new Date(checkout + 'T00:00:00'))
    return `${stayText} · ${from} – ${to}`
  }
  return `${stayText} · ${month === FLEX_ANYTIME ? 'Anytime' : monthLabel(month)}`
}
